/**
 * Super Admin Context
 * 
 * Provides authentication and state management for the super admin portal.
 * Completely separate from regular user authentication.
 */

import React, { createContext, useState, useContext, useEffect } from 'react';
import axios from 'axios';

const SuperAdminContext = createContext();

// Create a separate axios instance for super admin API calls
const superAdminApi = axios.create({
  baseURL: process.env.REACT_APP_API_URL || 'http://localhost:8000',
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add interceptor to include super admin token
superAdminApi.interceptors.request.use((config) => {
  const token = localStorage.getItem('superAdminToken');
  if (token) {
    config.headers['X-Super-Admin-Token'] = token;
  }
  return config;
});

// Handle responses — sync super admin session expiry from every API response
superAdminApi.interceptors.response.use(
  (response) => {
    // Try response header first, then body fields
    const expires = response.headers['x-super-admin-expires']
      || response.headers['x-token-expires-at']
      || response.data?.expires
      || response.data?.session_expires;
    if (expires) {
      localStorage.setItem('superAdminExpires', expires);
    }
    return response;
  },
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem('superAdminToken');
      localStorage.removeItem('superAdmin');
      localStorage.removeItem('superAdminExpires');
      window.location.href = '/superadmin/login';
    }
    return Promise.reject(error);
  }
);

export const SuperAdminProvider = ({ children }) => {
  const [superAdmin, setSuperAdmin] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    // Check for existing session on mount
    const token = localStorage.getItem('superAdminToken');
    const adminData = localStorage.getItem('superAdmin');

    if (token && adminData) {
      try {
        setSuperAdmin(JSON.parse(adminData));
        // Verify token is still valid
        verifySession();
      } catch (e) {
        logout();
      }
    }
    setLoading(false);
  }, []);

  const verifySession = async () => {
    try {
      const response = await superAdminApi.get('/api/superadmin/auth/me/');
      setSuperAdmin(response.data);
      localStorage.setItem('superAdmin', JSON.stringify(response.data));
    } catch (err) {
      logout();
    }
  };

  const login = async (username, password) => {
    setError(null);
    try {
      const response = await superAdminApi.post('/api/superadmin/auth/login/', {
        username,
        password,
      });

      const { token, admin, expires } = response.data;

      localStorage.setItem('superAdminToken', token);
      localStorage.setItem('superAdmin', JSON.stringify(admin));
      localStorage.setItem('superAdminExpires', expires);

      setSuperAdmin(admin);
      return { success: true };
    } catch (err) {
      const message = err.response?.data?.error || 'Login failed';
      setError(message);
      return { success: false, error: message };
    }
  };

  const logout = async () => {
    try {
      await superAdminApi.post('/api/superadmin/auth/logout/');
    } catch (err) {
      // Ignore logout errors
    }

    localStorage.removeItem('superAdminToken');
    localStorage.removeItem('superAdmin');
    localStorage.removeItem('superAdminExpires');
    setSuperAdmin(null);
  };

  const hasPermission = (permission) => {
    if (!superAdmin) return false;
    if (superAdmin.access_level === 'super') return true;

    const permissionMap = {
      admin: ['organizations', 'features', 'settings', 'users'],
      support: ['view_organizations', 'view_users', 'view_logs'],
      billing: ['billing', 'subscriptions', 'invoices'],
    };

    const allowed = permissionMap[superAdmin.access_level] || [];
    return allowed.includes(permission);
  };

  const value = {
    superAdmin,
    loading,
    error,
    login,
    logout,
    hasPermission,
    api: superAdminApi,
    isAuthenticated: !!superAdmin,
  };

  return (
    <SuperAdminContext.Provider value={value}>
      {children}
    </SuperAdminContext.Provider>
  );
};

export const useSuperAdmin = () => {
  const context = useContext(SuperAdminContext);
  if (!context) {
    throw new Error('useSuperAdmin must be used within a SuperAdminProvider');
  }
  return context;
};

export default SuperAdminContext;
